import { useMemo } from "react";
import { graphql, useFragment } from "react-relay";
import {
  StoreTier,
  useHasFeatureInTier_Store$key,
} from "./__generated__/useHasFeatureInTier_Store.graphql";

export enum Features {
  FILES = "FILES",
  CHECKOUT_MODE = "CHECKOUT_MODE",
  OPERATIONS_PLATFORM = "OPERATIONS_PLATFORM",
  FOLLOWUP_AND_HISTORY = "FOLLOWUP_AND_HISTORY",
  CONNECT = "CONNECT",
  INVITE_USERS = "INVITE_USERS",
}

const features = {
  free: [] as Features[],
  light: [Features.INVITE_USERS, Features.FILES],
  basic: [Features.OPERATIONS_PLATFORM, Features.INVITE_USERS, Features.FILES],
  standard: [
    Features.OPERATIONS_PLATFORM,
    Features.CHECKOUT_MODE,
    Features.FILES,
    Features.FOLLOWUP_AND_HISTORY,
    Features.CONNECT,
    Features.INVITE_USERS,
  ],
  premium: [
    Features.OPERATIONS_PLATFORM,
    Features.CHECKOUT_MODE,
    Features.FILES,
    Features.FOLLOWUP_AND_HISTORY,
    Features.CONNECT,
    Features.INVITE_USERS,
  ],
};

function assertNever(value: never) {
  return "standard" as const;
}

function coerceTier(tier: StoreTier): keyof typeof features {
  switch (tier) {
    case "free":
      return "free";
    case "light":
      return "light";
    case "basic":
      return "basic";
    case "standard":
      return "standard";
    case "premium":
    case "unlimited":
      return "premium";
  }

  return assertNever(tier);
}

export function hasFeatureInTier(feature: Features, tier: StoreTier) {
  return features[coerceTier(tier)].includes(feature);
}

export function useHasFeatureInTier(
  feature: Features,
  fragmentRef: useHasFeatureInTier_Store$key
) {
  const { tier } = useFragment(
    graphql`
      fragment useHasFeatureInTier_Store on Store {
        tier
      }
    `,
    fragmentRef
  );

  return useMemo(() => hasFeatureInTier(feature, tier), [feature, tier]);
}
